// Wire-shape parsing for the `/capture/items` POST dispatch: the POST 200 body
// the same-id LWW re-apply inspects, and the authoritative-row target carried on
// an `already_exists` 409. Extracted verbatim from connector-dispatch-create-item.ts
// (no behaviour change) to keep that dispatcher under the 500-line cap — pure,
// I/O-free defensive parsing of untrusted response/error bodies.

import { ApiError } from '../api'

/**
 * Minimal shape of the POST /capture/items 200 body the same-id LWW re-apply
 * inspects (audit-1 #2). `created: false` ⇒ the server returned a stored row
 * (idempotent / keep_existing); `item` carries its quantity + syncVersion. All
 * optional — defensively parsed because the body is untrusted at this layer.
 */
export interface CreateItemPostResult {
  readonly created?: boolean
  readonly item?: {
    readonly id?: string
    readonly quantity?: unknown
    /**
     * Stored row's confirmed-zero flag (audit-1-#1). The same-id re-apply must
     * compare it alongside quantity: a confirmed-zero create against a qty-0
     * template row has NO quantity delta, so a quantity-only gate silently
     * drops the surveyor's flag (ADR-021 captured work).
     */
    readonly isZeroConfirmed?: unknown
    /**
     * Stored row's notes (audit-2-#1). The third compare leg: qty and flag can
     * both match while the local note differs — the 409 branch and the PATCH
     * comparator (connector-self-inflicted-conflict.ts) both honour notes, so
     * the same-id gate must too or the note drops on the pure-ack path.
     */
    readonly notes?: unknown
    readonly syncVersion?: number
  }
}

/** The re-apply target carried on an already_exists error. */
export interface AuthoritativeTarget {
  readonly id: string
  readonly syncVersion: number
}

/**
 * Pull the authoritative row's id + syncVersion out of an
 * already_exists error body (`details.authoritative`, camelCase — the
 * same shape handleLwwConflict reads in connector.ts). Returns null if
 * the payload is missing or malformed so the caller can refuse to drop
 * the count silently. Defensive parsing because `err.body` is unknown.
 */
export function readAuthoritativeTarget(
  err: ApiError,
): AuthoritativeTarget | null {
  const body = err.body
  const details =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['details']
      : null
  const authoritative =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)['authoritative']
      : null
  if (typeof authoritative !== 'object' || authoritative === null) return null
  const row = authoritative as Record<string, unknown>
  const id = row['id']
  const syncVersion = row['syncVersion']
  if (typeof id !== 'string' || id === '') return null
  if (typeof syncVersion !== 'number') return null
  return { id, syncVersion }
}
